var CoverVariables             = require("app/theme/styles/covers/CoverVariables"),
    HeaderVariables            = require("app/theme/styles/headers/HeaderVariables"),
    Image                      = require("app/ui/Image"),
    Layout                     = require("./CoverLayout"),
    MediaPlayer                = require("app/ui/radiant/MediaPlayer"),
    PlayMediaButton            = require("app/MediaPlayer/PlayMediaButton"),
    ImageOptimizerSupport      = require("app/ui/ImageOptimizerSupport"),
    deepEqual                  = require("fast-deep-equal"),
    defaultVideoSetup          = require("app/ui/radiant/defaultVideoSetup"),
    findBestFittingCover       = require("app/utils/findBestFittingCover"),
    findBestFittingPlaceholder = require("app/utils/findBestFittingPlaceholder"),
    frameThrottle              = require("enyo-perf/utils").frameThrottle,
    get                        = require("object-path").get,
    kind                       = require("enyo/kind"),
    placeholders               = require("app/theme/assets/placeholders-notext");

require("./Cover.less");
require("app/theme/IconButton--cover-icon.less");

module.exports = kind(/** @lends  app.ui.header.Cover.prototype */ {

    /**
     * @private
     */
    name: "app.ui.header.Cover",

    /**
     * @private
     */
    classes: "app-ui-header-cover",

    /**
     * @private
     */
    layoutKind: Layout,

    /**
     * The formFactor of the cover.
     *
     * Available options:
     *  - `landscape`
     *  - `square`
     *  - `portrait`
     *  - `null` (no cover)
     *
     * @return   {?string}
     * @default  `null`
     * @public
     */
    formFactor: null,

    /**
     * @private
     */
    components: [{
        name:        "coverPlaceholder",
        kind:        Image,
        mixins:      [ImageOptimizerSupport],
        classes:     "app-ui-header-cover__placeholder",
        canGenerate: Boolean(HeaderVariables.useCoverPlaceholderImage)
    }, {
        name:    "coverImage",
        kind:    Image,
        mixins:  [ImageOptimizerSupport],
        classes: "app-ui-header-cover__image"
    }, {
        classes:    "app-ui-header-cover__player-container",
        components: [{
            name:        "player",
            kind:        MediaPlayer,
            classes:     "app-ui-header-cover__player",
            canGenerate: false
        }]
    }, {
        name:    "playMediaButton",
        kind:    PlayMediaButton,
        classes: "app-ui-header-cover__play-media-button enyo-fit",
        theme:   "cover-icon"
    }],

    /**
     * @method
     * @private
     */
    create: kind.inherit(function(sup) {
        return function() {
            this.onPlayerStateChange = this.bindSafely(this.onPlayerStateChange);
            this.onPlayerError       = this.bindSafely(this.onPlayerError);
            this.update              = frameThrottle(this.bindSafely(this.update));
            sup.apply(this, arguments);
            this.update();
        };
    }),

    /**
     * @method
     * @private
     */
    update: function() {
        var data        = this.data || null,
            formFactor  = this.get("formFactor") || null,
            aspectRatio = CoverVariables.getAspectRatio(formFactor),
            placeholder,
            cover       = formFactor ? findBestFittingCover(
                data || {},
                aspectRatio
            ) : null,
            videoCover  = get(data, "meta.video_cover", null),
            playerSetup = null;

        if (HeaderVariables.useCoverPlaceholderImage) {
            placeholder = formFactor ? findBestFittingPlaceholder(
                placeholders,
                aspectRatio
            ) : null;
            this.$.coverPlaceholder.set("rawImageSrc", placeholder ? {
                width:  placeholder.width || null,
                height: placeholder.height || null,
                ratio:  placeholder.aspect_ratio || null,
                src:    get(placeholder, "url", null)
            } : null);
        }

        this.$.coverImage.set("rawImageSrc", cover ? {
            width:  cover.width || null,
            height: cover.height || null,
            ratio:  cover.aspect_ratio || null,
            src:    get(cover, "url", null)
        } : null);

        // Player setup for current media item
        if (videoCover) {
            playerSetup     = this.createPlayerSetup();
            playerSetup.src = {
                hls: videoCover
            };
        }
        this.updatePlayer(playerSetup);
    },

    /**
     * Generate setup configuration for the player. Extend to apply media
     * source.
     *
     * @returns  {Object}  The setup
     * @protected
     */
    createPlayerSetup: function() {
        var setup;

        setup = Object.assign(
            {},
            defaultVideoSetup,
            {
                automaticFullscreenOnLandscape:       false,
                autoplay:                             true,
                disableKeyboardControl:               true,
                doubleClickForFullscreen:             false,
                forceInitialVolume:                   true,
                initialVolume:                        0,
                loop:                                 true,
                muted:                                true,
                pauseContentWhenViewingAreaIsClicked: false,
                permanentMuted:                       true,
                posterScaleMode:                      "zoom",
                rememberCurrentTime:                  false,
                rememberQuality:                      true,
                rememberVolume:                       false,
                requestAutoplayCapabilitiesData:      false,
                scaleMode:                            "zoom"
            }
        );

        return setup;
    },

    /**
     * Creates a new player with the new setup as needed.
     *
     * @param  {Object}  setup  The player setup
     * @private
     */
    updatePlayer: function(setup) {
        var $player = this.$.player;

        // New player needed
        if (!deepEqual(setup, $player.get("playerSetup"))) {
            // Destroy existing player as needed
            $player.hasPlayer() && $player.destroyPlayer();

            // Bail if no setup is provided
            if (!setup) {
                return;
            }

            // Setup new player
            $player.set("playerSetup", setup);
            $player.set("playerEventListeners", [
                ["playerStateChanged", this.onPlayerStateChange],
                ["setupError", this.onPlayerError],
                ["playerError", this.onPlayerError],
                ["fatalError", this.onPlayerError]
            ]);
            $player.set("canGenerate", true);
            $player.applyStyle("opacity", 0);

            // If our cover is rendered already, create the new player
            // explicitly. Otherwise, it'll get created at render time.
            if (this.hasNode()) {
                $player.hasNode() ? $player.createPlayer() : $player.render();
            }
        }
    },


    /**
     * PlayerApi broadcasted a new player state.
     *
     * @param  {Object}  event  The event
     * @private
     */
    onPlayerStateChange: function(event) {
        var $player = this.$.player,
            state   = event.state || {};

        if (state.isReady === 1) {
            $player.getPlayerApi().hideControls();
            $player.applyStyle("opacity", 1);
        }

        if (state.playerState === "ended") {
            $player.applyStyle("opacity", 0);
        }
    },

    /**
     * The player broadcasted an error.
     *
     * @param  {Object}  event  The error event
     * @private
     */
    onPlayerError: function() {
        var $player = this.$.player;

        $player.set("canGenerate", false);
        $player.applyStyle("opacity", 0);
        $player.destroyPlayer();
    }

});
